import type { Lead, ScoreFactor } from "@/mocks/leadsData";
import { getScoreBadgeColors, getScoreLabel } from "@/mocks/leadsData";

type Props = {
  lead: Lead;
};

export function LeadScoreCard({ lead }: Props) {
  const score = typeof lead.score === "number" && !isNaN(lead.score) ? Math.min(100, Math.max(0, Math.round(lead.score))) : 50;
  const category = getScoreLabel(score);
  const badgeColors = getScoreBadgeColors(score);

  // SVG ring calculation — circumference = 2 * PI * r (r=54)
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  const ringColor = score >= 71 ? "#16a34a" : score >= 41 ? "#d97706" : "#dc2626";

  const leadFactors = Array.isArray(lead.scoreFactors) && lead.scoreFactors.length > 0
    ? lead.scoreFactors
    : Array.isArray((lead as any).score_factors) && (lead as any).score_factors.length > 0
    ? (lead as any).score_factors
    : null;

  // If scoreFactors are not provided from DB, compute dynamic factors based on lead data
  const rawFactors: ScoreFactor[] = leadFactors || [
    {
      label: "Contact & Profile Completeness",
      score: Math.round(((lead.phone ? 10 : 0) + (lead.email ? 10 : 0) + (lead.company ? 5 : 0)) * (score / 75)),
      max: 25,
    },
        {
          label: "Source Quality & Channel",
          score: Math.round(
            (lead.source === "Website" || lead.source === "WhatsApp" ? 22 : lead.source === "Manual" ? 16 : 20) *
              (score / 80)
          ),
          max: 25,
        },
        {
          label: "Engagement & Priority",
          score: Math.round(
            (lead.priority === "High" ? 23 : lead.priority === "Medium" ? 17 : 12) * (score / 75)
          ),
          max: 25,
        },
        {
          label: "Pipeline Stage & Activity",
          score: Math.round(
            (lead.stage === "Booking Done" || lead.stage === "Proposal Sent" ? 24 : 16) * (score / 75)
          ),
          max: 25,
        },
      ];

  const factors: ScoreFactor[] = rawFactors.map((f) => ({
    ...f,
    score: Math.min(f.max, Math.max(0, f.score)),
  }));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 md:p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">AI Lead Score</h3>
        <span className="text-[11px] font-medium text-gray-400">Updated today</span>
      </div>

      {/* Large score circle */}
      <div className="flex flex-col items-center mb-5">
        <div className="relative w-32 h-32">
          <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r={radius}
              fill="none"
              stroke="#f3f4f6"
              strokeWidth="8"
            />
            <circle
              cx="60"
              cy="60"
              r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className="transition-all duration-700 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-gray-900">{score}</span>
            <span className="text-[11px] font-medium text-gray-400">/ 100</span>
          </div>
        </div>

        <span className={`mt-2 px-3 py-0.5 rounded-full text-xs font-semibold ${badgeColors}`}>
          {category} Lead
        </span>

        <p
          className={`text-xs mt-1.5 flex items-center gap-1 font-medium ${
            score >= 71 ? "text-green-600" : score >= 41 ? "text-amber-600" : "text-gray-500"
          }`}
        >
          <i className={score >= 41 ? "ri-arrow-up-line text-[10px]" : "ri-subtract-line text-[10px]"} />
          {score >= 71 ? "+8 points this week" : score >= 41 ? "+3 points this week" : "Stable score"}
        </p>
      </div>

      {/* Score breakdown table */}
      <div className="space-y-3 pt-3 border-t border-gray-100">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Score Breakdown</p>
        <div className="flex items-center justify-between text-[11px] font-semibold text-gray-400 pb-1 border-b border-gray-100">
          <span>Factor</span>
          <span className="flex items-center gap-4">
            <span>Score</span>
            <span>Max</span>
          </span>
        </div>
        {factors.map((factor: ScoreFactor, idx: number) => {
          const max = factor.max || 25;
          const scoreVal = factor.score || 0;
          const pct = Math.min(100, Math.max(0, Math.round((scoreVal / max) * 100)));
          let barColor = "bg-indigo-500";
          if (pct >= 75) barColor = "bg-green-500";
          else if (pct >= 45) barColor = "bg-amber-500";
          else barColor = "bg-red-400";

          return (
            <div key={idx}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600 truncate max-w-[200px]" title={factor.label}>
                  {factor.label}
                </span>
                <span className="flex items-center gap-4 text-xs text-gray-700 font-mono">
                  <span className="font-semibold text-gray-900">{scoreVal}</span>
                  <span className="text-gray-400">{max}</span>
                </span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full rounded-full ${barColor} transition-all duration-500`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}